import React, { useEffect, useState } from 'react';
import NoDataFound from '../../NoDataFound/NoDataFound';
import ChartFormatIndianNumber from "../../../Common/ChartFormatIndianNumber";
import useThemeTextColor from '../../../../../utils/useThemeTextColor';
import ReactApexChart from 'react-apexcharts';

// Global weekday map
const weekdayLabelMap = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

const TimeSalesChart = ({ chartData }) => {
  const textColor = useThemeTextColor();
  const borderColor = useThemeTextColor('--why-choose-card-border');

  const [labels, setLabels] = useState([]);
  const [quantityData, setQuantityData] = useState([]);
  useEffect(() => {
    if (!Array.isArray(chartData) || chartData.length === 0) return;

    const keys = Object.keys(chartData[0]);
    let parsedLabels = [];
    let parsedValues = [];

    if (keys.includes('weekday')) {
      const dataMap = chartData.reduce((acc, item) => {
        acc[item.weekday] = item.actual_units;
        return acc;
      }, {});

      const fullWeekData = Array.from({ length: 7 }, (_, i) => {
        const weekday = i + 1;
        return {
          label: weekdayLabelMap[weekday],
          value: dataMap[weekday] || 0,
        };
      });

      parsedLabels = fullWeekData.map((item) => item.label);
      parsedValues = fullWeekData.map((item) => item.value);

    } else if (keys.includes('hour')) {
      const sorted = [...chartData].sort((a, b) => a.hour - b.hour);
      parsedLabels = sorted.map((item) => `${item.hour}`);
      parsedValues = sorted.map((item) => item.actual_units);

    } else if (keys.includes('daypart')) {
      parsedLabels = chartData.map((item) => item.daypart);
      parsedValues = chartData.map((item) => item.actual_units);

    } 
     else if (keys.includes('month')) {
      parsedLabels = chartData.map((item) => {
        const date = new Date(item.month + '-01');
        return date.toLocaleString('default', { month: 'short', year: 'numeric' });
      });
      parsedValues = chartData.map((item) => item.actual_units);

    }
    else if (keys.includes('quarter')) {
      parsedLabels = chartData.map((item) => `Q${item.quarter}`);
      parsedValues = chartData.map((item) => item.actual_units);
    }

    setLabels(parsedLabels);
    setQuantityData(parsedValues);
  }, [chartData]);

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return <NoDataFound height={300} />;
  }

  const series = [
    {
      name: 'Actual Units',
      data: quantityData,
    },
  ];

  const options = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: 'easeout',
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
        borderRadius: 6,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: labels,
      labels: {
        style: { colors: textColor, fontSize: '12px' },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: textColor, fontSize: '12px' },
        formatter: (val) => ChartFormatIndianNumber(val),
      },
    },
    grid: {
      borderColor: borderColor,
      strokeDashArray: 2,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    tooltip: {
      y: {
        formatter: (val) => `${ChartFormatIndianNumber(val)}`,
      },
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: ['#0072ff'],
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 1,
        stops: [0, 100],
      },
    },
    legend: {
      show: false,
    },
  };

  return (
    <div style={{ width: '100%', height: '360px' }}>
      <ReactApexChart options={options} series={series} type="bar" height={350} />
    </div>
  );
};

export default React.memo(TimeSalesChart);
